import { z } from 'zod';
import ValidatorUtils from 'services/ValidatorUtils';

/**
 * Reusable Zod schemas wrapping existing ValidatorUtils validators.
 * Each schema uses `.superRefine` to reuse the existing validation logic
 * so error messages stay consistent across the app.
 */

export const requiredString = z.string().min(1, 'Required');

export const optionalString = z.string().optional().or(z.literal(''));

export const emailSchema = z.string().superRefine((val, ctx) => {
  const error = ValidatorUtils.validateEmail(val);
  if (error) ctx.addIssue({ code: z.ZodIssueCode.custom, message: error });
});

export const usernameSchema = z
  .string()
  .transform((val) => val.toLowerCase().replaceAll(' ', ''))
  .pipe(
    z.string().superRefine((val, ctx) => {
      const error = ValidatorUtils.validateUsername(val);
      if (error) ctx.addIssue({ code: z.ZodIssueCode.custom, message: error });
    }),
  );

export const passwordSchema = z.string().superRefine((val, ctx) => {
  const error = ValidatorUtils.validatePassword(val);
  if (error) ctx.addIssue({ code: z.ZodIssueCode.custom, message: error });
});

export const postcodeSchema = z.string().superRefine((val, ctx) => {
  const error = ValidatorUtils.validatePostcode(val);
  if (error) ctx.addIssue({ code: z.ZodIssueCode.custom, message: error });
});

export const verificationCodeSchema = z.string().superRefine((val, ctx) => {
  const error = ValidatorUtils.validateVerificationCode(val);
  if (error) ctx.addIssue({ code: z.ZodIssueCode.custom, message: error });
});

export const ohipSchema = z.string().superRefine((val, ctx) => {
  const error = ValidatorUtils.validateOhip(val);
  if (error) ctx.addIssue({ code: z.ZodIssueCode.custom, message: error });
});

export const optionalDate = z.number().optional();

export const dollarAmountSchema = z.string().superRefine((val, ctx) => {
  if (!val || !ValidatorUtils.isDollarAmount(val)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'must be a number' });
  }
});

/**
 * Helper to create a password confirmation schema.
 * Use with z.object().refine() to validate that two password fields match.
 */
export function passwordsMatch(passwordField: string, confirmField: string) {
  return (data: Record<string, unknown>) => data[passwordField] === data[confirmField];
}

export const passwordsMatchMessage = {
  message: 'Passwords do not match',
  path: ['confirmPassword'],
};
